/**
 * Inventory Tenant - Utility Functions
 */

import { Endereco } from "../types";

export function getFormData(object: Record<string, unknown>): FormData {
    const formData = new FormData();
    Object.keys(object).forEach(key => {
        const value = object[key];
        if (value !== null && value !== undefined) {
            formData.append(key, String(value));
        }
    });
    return formData;
}

export function getAge(birthDate: string, deathDate: string | null = null): number | 'N/A' {
    const deathDateAsDate = deathDate ? new Date(deathDate) : new Date();

    return birthDate && deathDateAsDate
                    ? Math.floor((deathDateAsDate.getTime() - new Date(birthDate).getTime()) / (1000 * 60 * 60 * 24 * 365.25))
                    : 'N/A'
}

export function formatDate(dateString: string): string {
    try {
        if (!dateString) return '';

        // Aceita vários formatos: ISO string, yyyy-mm-dd, etc.
        const date = new Date(dateString);
        
        // Verifica se a data é válida
        if (isNaN(date.getTime())) {
            // Tenta formato yyyy-mm-dd
            if (/^\d{4}-\d{2}-\d{2}$/.test(dateString)) {
                const [year, month, day] = dateString.split('-').map(Number);
                const parsedDate = new Date(year, month - 1, day);
                if (!isNaN(parsedDate.getTime())) {
                    return parsedDate.toLocaleDateString('pt-BR', {
                        year: 'numeric',
                        month: 'long',
                        day: 'numeric'
                    });
                }
            }
            return dateString;
        }

        return date.toLocaleDateString('pt-BR', {
            year: 'numeric',
            month: 'long',
            day: 'numeric'
        });
    } catch (error) {
        console.error('Erro ao formatar data:', error);
        return dateString;
    }
}

/**
 * Formata data para formato curto (dd/mm/yyyy)
 */
export function formatDateShort(dateString: string): string {
    try {
        if (!dateString) return '';

        const date = new Date(dateString);
        if (isNaN(date.getTime())) {
            if (/^\d{4}-\d{2}-\d{2}$/.test(dateString)) {
                const [year, month, day] = dateString.split('-').map(Number);
                const parsedDate = new Date(year, month - 1, day);
                if (!isNaN(parsedDate.getTime())) {
                    return parsedDate.toLocaleDateString('pt-BR');
                }
            }
            return dateString;
        }

        return date.toLocaleDateString('pt-BR');
    } catch (error) {
        console.error('Erro ao formatar data curta:', error);
        return dateString;
    }
}

export function formatCurrency(value: number): string {
    return value ? new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(Number(value)) : '-';
}

export function formatPercentage(value: number): string {
    return value ? `${value}%` : '-';
}

/** Últimos 4 caracteres do id do inventário (exibição em impressão e busca). */
export function formatInventoryIdentifierShort(id: string | number | null | undefined): string {
    if (id == null) return '';
    const s = String(id).trim();
    if (!s) return '';
    return s.slice(-4);
}

/** Verifica se o termo de busca corresponde ao identificador (completo ou sufixo de 4 caracteres). */
export function inventoryMatchesIdentifierSearch(
    id: string | number | null | undefined,
    searchTerm: string,
): boolean {
    if (id == null) return false;
    const term = searchTerm.trim().toLowerCase();
    if (!term) return false;

    const idStr = String(id).trim().toLowerCase();
    const shortId = formatInventoryIdentifierShort(id).toLowerCase();

    if (idStr.includes(term)) return true;
    if (shortId.includes(term)) return true;
    if (term.length <= 4 && idStr.endsWith(term)) return true;
    return false;
}

export function formatAddress(endereco: Endereco): string {
    if (!endereco.rua) return '-';

    const parts = [
        endereco.rua,
        endereco.cidade && endereco.estado ? `${endereco.cidade} - ${endereco.estado}` : endereco.cidade || endereco.estado,
        endereco.cep
    ].filter(Boolean);

    return parts.join(', ');
}

